import { dayjs } from '@abyss/web/tools/dayjs';
import { storage } from '@abyss/web/tools/storage';
import { styled } from '@abyss/web/tools/styled';
import { createTheme } from '@abyss/web/tools/theme';
import { V2Alert } from '@abyss/web/ui/Alert';
import { OverlayProvider } from '@abyss/web/ui/OverlayProvider';
import { PrintProvider } from '@abyss/web/ui/PrintProvider';
import { RouterProvider } from '@abyss/web/ui/RouterProvider';
import { ThemeProvider } from '@abyss/web/ui/ThemeProvider';
import { ToastProvider } from '@abyss/web/ui/Toast';
import find from 'lodash/find';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Constants } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../common/Utils';
import { dateKey } from '../../common/Utils/datesUtils/formatDate';
import { CountySearchContextProvider } from '../../context/CountySearchContext';
import { SearchFilterContextProvider } from '../../context/SearchFilterContext';
import { ErrorBoundary } from '../../errors/ErrorBoundary';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useSupportedLanguages } from '../../hooks/useSupportedLanguages';
import { registerEvents } from '../../tools/events';
import { loginNotRequired, validateSession } from '../../utils/session.utils';
import { uhcThemeOverride } from '../themeOverrides/uhcThemeOverride';
import { Analytics } from './context/Analytics';
import { CareTeamConfig } from './context/CareTeamConfig/CareTeamConfig';
import { Configuration } from './context/Configuration';
import { DateTimeConfig } from './context/DateTimeConfig';
import { EducationalContentModal } from './context/EducationalContentModal/EducationalContentModal';
import { GlobalNav } from './context/GlobalNav';
import { Internationalization } from './context/Internationalization';
import { LanguageNotSupportedModal } from './context/LanguageNotSupportedModal/LanguageNotSupportedModal';
import { LivePerson } from './context/LivePerson';
import { Obapi } from './context/Obapi';
import { PlanSelector } from './context/PlanSelector';
import { SearchLocation } from './context/SearchLocation';
import { TimeoutModal } from './context/TimeoutModal';
import { createRouter } from './createRouter';
import { Routes } from './routes';

const theme = createTheme('uhc', uhcThemeOverride);

const StickyAlertContainer = styled('div', {
  position: 'sticky',
  top: 0,
  zIndex: 998,
  backgroundColor: 'white',
});

export const App = () => {
  const sessionId = useStore(StoreKeys.SESSION_ID);
  const setSessionId = useStore(StoreKeys.SET_SESSION_ID);
  const location = useLocation();
  const isStandAloneExp = loginNotRequired();

  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();
  const enableTimeTesting = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIMEMACHINE_TESTING
  );
  const showOutageBanner = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_OUTAGE_BANNER
  );

  const outageBannerUpdatedTime = dayjs(
    featureFlags?.find(
      (flag) => flag.key === ConstantsLagoon.FEATURE_FLAGS.ENABLE_OUTAGE_BANNER
    )?.[dateKey]
  ).format('MMMM DD, YYYY HH:mm:ss');

  const showTimeStamp =
    featureFlags?.find(
      (flag) => flag.key === ConstantsLagoon.FEATURE_FLAGS.ENABLE_OUTAGE_BANNER
    )?.test ?? false;

  const data = useLagoon('ui-messaging')();
  const outageBannerContent = find(data, {
    key: ConstantsLagoon.OUTAGE_BANNER.ENABLE_OUTAGE_BANNER_MESSAGE,
  });
  const supportedLanguages: string[] = useSupportedLanguages();
  const [isFirstTimeForCareTeam, setIsFirstTimeForCareTeam] = useState(true);

  const handleValidateSession = () => {
    if (!enableTimeTesting) {
      validateSession(sessionId, setSessionId);
    }
  };

  useEffect(() => {
    // Validate on window focus
    window.addEventListener('focus', handleValidateSession);
    return () => {
      window.removeEventListener('focus', handleValidateSession);
    };
  }, []);

  useEffect(() => {
    // Validate on location change
    handleValidateSession();
  }, [location]);

  const { IS_PSX_VIEWED } = Constants.STORAGE_KEYS.LOCAL;

  useEffect(() => {
    handleValidateSession();
    storage.local.set(IS_PSX_VIEWED, true);
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.SUPPORTED_LANGUAGES,
      JSON.stringify(Array.from(supportedLanguages))
    );
  }, []);

  return (
    <ErrorBoundary>
      <PlanSelector>
        <DateTimeConfig>
          <Analytics>
            <Obapi>
              <Internationalization>
                <GlobalNav>
                  <PrintProvider>
                    <OverlayProvider>
                      <ThemeProvider theme={theme}>
                        {!isStandAloneExp && <TimeoutModal />}
                        <ToastProvider
                          containerWidth="26rem"
                          position="top-right"
                        />
                        <EducationalContentModal />
                        <CareTeamConfig
                          isFirstTimeForCareTeam={isFirstTimeForCareTeam}
                          setIsFirstTimeForCareTeam={setIsFirstTimeForCareTeam}
                        />
                        <LanguageNotSupportedModal />
                        <LivePerson />
                        <SearchFilterContextProvider>
                          <CountySearchContextProvider>
                            {showOutageBanner && (
                              <StickyAlertContainer>
                                <V2Alert
                                  dismissible={false}
                                  showDivider={false}
                                  status="warning"
                                  {...(showTimeStamp && {
                                    timestamp: outageBannerUpdatedTime,
                                  })}
                                  title={outageBannerContent?.title || ''}
                                >
                                  <div>{outageBannerContent?.message}</div>
                                </V2Alert>
                              </StickyAlertContainer>
                            )}
                            <SearchLocation>
                              <Routes />
                            </SearchLocation>
                          </CountySearchContextProvider>
                        </SearchFilterContextProvider>
                      </ThemeProvider>
                    </OverlayProvider>
                  </PrintProvider>
                </GlobalNav>
              </Internationalization>
            </Obapi>
          </Analytics>
        </DateTimeConfig>
      </PlanSelector>
    </ErrorBoundary>
  );
};

const router = createRouter(App);

export const ProviderSearch = () => {
  useEffect(() => {
    const loadAdobeScript = true;
    registerEvents(undefined, loadAdobeScript);
  }, []);

  return (
    <ErrorBoundary>
      <Configuration>
        <RouterProvider router={router} />
      </Configuration>
    </ErrorBoundary>
  );
};
